import type { FC, ReactNode } from 'react'
import type { Project } from '@/types'

import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ProjectCardProps {
    project: Project
}

// A project opens its live link in a new tab when one is set in the CMS.
// Without a link the card is a plain showcase tile.
export const ProjectCard: FC<ProjectCardProps> = ({ project }): ReactNode => {
    const linked = Boolean(project.link)

    const content = (
        <>
            <div className='relative aspect-square overflow-hidden rounded-t-card bg-secondary'>
                {project.image
                    ? (
                        <img
                            src={project.image}
                            alt={project.imageAlt ?? ''}
                            loading='lazy'
                            decoding='async'
                            className='size-full object-cover transition-transform duration-700 ease-out group-hover:scale-105'
                        />
                    )
                    : <div className='size-full bg-linear-to-br from-brand-blush via-brand-mist to-brand-haze' aria-hidden='true' />}

                {linked && (
                    <span
                        aria-hidden='true'
                        className='absolute top-4 right-4 flex size-16 items-center justify-center rounded-panel border border-border bg-background p-[1.2rem] lg:size-24 lg:p-[1.8rem]'
                    >
                        <ArrowRight className='size-full -rotate-45 text-primary transition-transform duration-500 ease-out group-hover:rotate-0' strokeWidth={2} />
                    </span>
                )}
            </div>

            <div className='flex flex-col gap-2.5 pt-2.5'>
                {(project.client || project.category) && (
                    <p className='mt-2.5 flex flex-wrap items-center gap-2.5 text-base leading-[1.3]'>
                        {project.client && (
                            <span className='font-semibold' data-mdlfy-cms={`projects:${project.id}:client`}>
                                {project.client}
                            </span>
                        )}

                        {project.category && (
                            <span data-mdlfy-cms={`projects:${project.id}:category`}>
                                {project.category}
                            </span>
                        )}
                    </p>
                )}

                <h3
                    className={cn('font-heading text-heading-4 font-normal transition-colors duration-300', linked && 'group-hover:text-primary')}
                    data-mdlfy-cms={`projects:${project.id}:title`}
                >
                    {project.title}
                </h3>
            </div>
        </>
    )

    if (project.link) {
        return (
            <a
                href={project.link}
                target='_blank'
                rel='noopener noreferrer'
                className='group block rounded-t-card focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary'
            >
                {content}
            </a>
        )
    }

    return (
        <article className='group'>
            {content}
        </article>
    )
}
